import React, { createContext, useState, useEffect, useCallback } from "react";

const UnsavedDataContext = createContext();

const LOCALSTORAGE_UNSAVED_SECTIONS = "unsavedSections";
const LOCALSTORAGE_UNSAVED_DATA_PREFIX = "unsavedData_";
const LOCALSTORAGE_ASK_NAV_CONFIRM = "askNavigateConfirmation";

export const UnsavedDataProvider = ({ children }) => {
  // Load unsaved sections from localStorage on init
  const [unsavedSections, setUnsavedSections] = useState(() => {
    try {
      return (
        JSON.parse(localStorage.getItem(LOCALSTORAGE_UNSAVED_SECTIONS)) || {}
      );
    } catch {
      return {};
    }
  });

  // Navigation confirmation flag (sync with localStorage)
  const [askNavigateConfirmation, setAskNavigateConfirmationState] = useState(
    () => {
      try {
        return (
          JSON.parse(localStorage.getItem(LOCALSTORAGE_ASK_NAV_CONFIRM)) ||
          false
        );
      } catch {
        return false;
      }
    }
  );

  // Keep localStorage in sync with unsavedSections
  useEffect(() => {
    localStorage.setItem(
      LOCALSTORAGE_UNSAVED_SECTIONS,
      JSON.stringify(unsavedSections)
    );
  }, [unsavedSections]);

  // Keep localStorage in sync with askNavigateConfirmation
  const setAskNavigateConfirmation = useCallback((value) => {
    setAskNavigateConfirmationState(value);
    localStorage.setItem(LOCALSTORAGE_ASK_NAV_CONFIRM, JSON.stringify(value));
  }, []);

  // Synchronous getter for askNavigateConfirmation
  const hasAskNavigateConfirmation = useCallback(() => {
    try {
      return (
        JSON.parse(localStorage.getItem(LOCALSTORAGE_ASK_NAV_CONFIRM)) || false
      );
    } catch {
      return false;
    }
  }, []);

  // Synchronous check for unsaved changes (for navigation guards, etc.)
  const hasUnsavedChanges = useCallback(() => {
    try {
      const sections =
        JSON.parse(localStorage.getItem(LOCALSTORAGE_UNSAVED_SECTIONS)) || {};
      return Object.values(sections).some(Boolean);
    } catch {
      return false;
    }
  }, []);

  // Set/clear unsaved status for a section
  const setSectionUnsaved = useCallback((section, isUnsaved) => {
    setUnsavedSections((prev) => {
      const updated = { ...prev, [section]: isUnsaved };
      localStorage.setItem(
        LOCALSTORAGE_UNSAVED_SECTIONS,
        JSON.stringify(updated)
      );
      return updated;
    });
  }, []);

  // Optionally, store/retrieve unsaved data snapshot for a section
  const setUnsavedData = useCallback((section, data) => {
    if (data === null || data === undefined) {
      localStorage.removeItem(LOCALSTORAGE_UNSAVED_DATA_PREFIX + section);
    } else {
      localStorage.setItem(
        LOCALSTORAGE_UNSAVED_DATA_PREFIX + section,
        JSON.stringify(data)
      );
    }
  }, []);

  const getUnsavedData = useCallback((section) => {
    try {
      return JSON.parse(
        localStorage.getItem(LOCALSTORAGE_UNSAVED_DATA_PREFIX + section)
      );
    } catch {
      return null;
    }
  }, []);

  // Clear all unsaved status and data (e.g., after successful save or logout)
  const clearAllUnsaved = useCallback(() => {
    setUnsavedSections({});
    localStorage.removeItem(LOCALSTORAGE_UNSAVED_SECTIONS);
    // Optionally, clear all unsaved data for all sections
    Object.keys(localStorage)
      .filter((key) => key.startsWith(LOCALSTORAGE_UNSAVED_DATA_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  }, []);

  return (
    <UnsavedDataContext.Provider
      value={{
        unsavedSections,
        setSectionUnsaved,
        hasUnsavedChanges,
        setUnsavedData,
        getUnsavedData,
        clearAllUnsaved,
        askNavigateConfirmation,
        setAskNavigateConfirmation,
        hasAskNavigateConfirmation,
      }}
    >
      {children}
    </UnsavedDataContext.Provider>
  );
};

export default UnsavedDataContext;
